import { PlaygroundUserMessage } from 'src/components/PlaygroundChat/PlaygroundUserMessage/PlaygroundUserMessage';
import { PlaygroundAIMessage } from 'src/components/PlaygroundChat/PlaygroundAIMessage/PlaygroundAIMessage';
import { clsx, type ClassValue } from 'clsx';
import React, { useEffect, useRef, useState, useCallback } from 'react';
import { ReplyPlaygroundButton } from 'src/components/PlaygroundChat/ReplyPlaygroundButton/ReplyPlaygroundButton';
import { sendSSEMessage } from 'src/utils/sse.utils';
import {
  appendPlaygroundMessages,
  clearPlayground,
  setPlayground,
  updateLastPlaygroundMessage,
} from 'src/store/slices/playgroundSlices';
import { PATH_PLAYGROUND_API } from 'src/constants/api-routes';
import { getEnvironment } from 'src/env/env';
import {
  useCreatePlaygroundChatMutation,
  useGetProjectByIdQuery,
  useLazyGetPlaygroundChatQuery,
} from 'src/services';
import { useAppDispatch, useAppSelector } from 'src/store';
import { useNotification } from 'src/hooks/useNotification';
import { uuid } from 'src/utils/uuid';
import moment from 'moment/moment';
import { RefreshPlaygroundButton } from 'src/components/PlaygroundChat/RefreshPlaygroundButton/RefreshPlaygroundButton';
import { useParams } from 'react-router-dom';
import { PlaygroundDateMessage } from 'src/components/PlaygroundChat/PlaygroundDateMessage/PlaygroundDateMessage';
import { PlaygroundSettingsVersion } from 'src/components/PlaygroundChat/PlaygroundSettingsVersion/PlaygroundSettingsVersion';
import { PlaygroundResolvedMessage } from 'src/components/PlaygroundChat/PlaygroundResolvedMessage/PlaygroundResolvedMessage';
import { PlaygroundEscalatedMessage } from 'src/components/PlaygroundChat/PlaygroundEscalatedMessage/PlaygroundEscalatedMessage';
import { ERROR_MESSAGE_PLAYGROUND, ISO_DATE_FORMAT } from 'src/shared/constants';
import { phEvent } from 'src/utils/posthog.utils';
import { FullScreenButton } from 'src/components/ButtonWithIcon/FullScreenButton';
import { getErrorMessage } from 'src/utils/get-error-message';
import { getApiVersion } from 'src/utils/apiVersion';
import { buildPlaygroundTimeline } from './playgroundTimeline';
import type { ConfigurationSettings } from 'src/services/configurationService';
import type { PlaygroundMessage } from 'src/components/PlaygroundChat/playgroundMessage';
import type { PlaygroundData } from 'src/store/slices/playgroundSlices';
import { type DateDivider, isDateDivider } from 'src/components/PlaygroundChat/playgroundTimeline';
import { useLoaderWhile } from 'src/hooks/useLoaderWhile';

const INITIAL_MESSAGE = `Hi there 👋 \n\n This is the Playground — a safe space to test customer conversations. Pretend to be your customer and ask me anything.`;

interface PlaygroundChatProps {
  // Keys this chat's messages in the playground slice, so each page keeps its own history.
  name?: string;
  className?: string;
  isTitle?: boolean;
  titleClassName?: string;
  isFullScreenBtn?: boolean;
  fullScreenBtnClick?: () => void;
  // The configuration version to chat with (sent as settings_id) and to show as a badge.
  settings?: ConfigurationSettings | null;
  disabled?: boolean;
  userMessageClassName?: string;
  aIMessageClassName?: string;
  chatContainerClassName?: ClassValue;
  replyContainerClassName?: ClassValue;
  versionHidden?: boolean;
}

export const PlaygroundChat = ({
  name = '',
  className = '',
  isTitle = false,
  titleClassName = '',
  isFullScreenBtn = false,
  fullScreenBtnClick = () => {},
  settings = null,
  disabled = false,
  userMessageClassName = '',
  aIMessageClassName = '',
  chatContainerClassName = '',
  replyContainerClassName = '',
  versionHidden = false,
}: PlaygroundChatProps) => {
  const { id } = useParams();
  const [isLastMessageLoading, setIsLastMessageLoading] = useState(false);
  const [inputValue, setInputValue] = useState('');
  // The chat as rendered: its messages interleaved with day dividers.
  const [chat, setChat] = useState<{
    _id?: string;
    status?: string;
    messages: Array<PlaygroundMessage | DateDivider>;
  } | null>(null);
  const [escalatedAt, setEscalatedAt] = useState('');
  const [resolvedAt, setResolvedAt] = useState('');
  const chatContainerRef = useRef(null);
  const textareaRef = useRef(null);
  const notification = useNotification();
  const { playground, PLAYGROUND_PREFIX } = useAppSelector((s) => s.playgroundSlices);
  const dispatch = useAppDispatch();
  const { managedAccountId } = useAppSelector((s) => s.companySlices);
  const prefix = name ? `${name}_` : PLAYGROUND_PREFIX;

  const { data: projectData } = useGetProjectByIdQuery({
    id: id,
  });

  const [
    createPlaygroundChat,
    {
      data: createPlaygroundChatData,
      error: createPlaygroundChatError,
      isLoading: isLoadingCreatePlaygroundChat,
    },
  ] = useCreatePlaygroundChatMutation();
  const [
    fetchPlaygroundChat,
    { data: getPlaygroundChatData, isLoading: isLoadingGetPlaygroundChat },
  ] = useLazyGetPlaygroundChatQuery();

  const sendMessage = useCallback(
    async (message: string) => {
      const environment = await getEnvironment();
      const version = getApiVersion();
      const urlStr = `${environment.APP_BASE_URL}/${version}/${PATH_PLAYGROUND_API.ROOT}/${id}${PATH_PLAYGROUND_API.CHAT}/${chat._id}${PATH_PLAYGROUND_API.MESSAGES}`;
      const url = new URL(urlStr);

      if (settings?.id) {
        url.searchParams.set('settings_id', String(settings.id));
      }

      if (managedAccountId) {
        url.searchParams.set('account', managedAccountId);
      }

      const chatData = playground?.[`${prefix}${id}`];

      if (chatData?.messages?.length < 2) {
        phEvent('send_playground_message');
      }

      sendSSEMessage('POST', url.toString(), message, (response) => {
        const { error, message } = response;

        if (error) {
          notification.error(error);
          dispatch(updateLastPlaygroundMessage({ prefix, projectId: id, message: null }));
        } else {
          dispatch(updateLastPlaygroundMessage({ prefix, projectId: id, message }));
        }
      });
    },
    [id, chat, settings, managedAccountId],
  );

  useEffect(() => {
    const chatData = playground?.[`${prefix}${id}`];

    if (chatData) {
      fetchPlaygroundChat({ projectId: id, chatId: chatData._id });
    } else {
      createPlaygroundChat({ projectId: id });
    }
  }, [id]);

  useEffect(() => {
    const chatData = playground[`${prefix}${id}`];

    if (!chatData) {
      return;
    }

    if (!chatData.messages?.length) {
      const message = createMessage(INITIAL_MESSAGE, 'ai', chatData);

      dispatch(appendPlaygroundMessages({ prefix, projectId: id, messages: [message] }));
    }

    const timeline = buildPlaygroundTimeline(chatData.messages, chatData.status);

    setEscalatedAt(timeline.escalatedAt);
    setResolvedAt(timeline.resolvedAt);
    setChat({ ...chatData, messages: timeline.items });
  }, [playground]);

  useEffect(() => {
    const lastItem = chat?.messages?.[chat.messages.length - 1];
    const isLoading = lastItem && !isDateDivider(lastItem) && lastItem.loading;
    scrollToBottom();
    setIsLastMessageLoading(Boolean(isLoading));
  }, [chat]);

  useEffect(() => {
    if (createPlaygroundChatData) {
      dispatch(setPlayground({ prefix, projectId: id, playground: createPlaygroundChatData }));
    }
  }, [createPlaygroundChatData]);

  useEffect(() => {
    if (getPlaygroundChatData) {
      const message = createMessage(INITIAL_MESSAGE, 'ai', getPlaygroundChatData);
      const messages = getPlaygroundChatData.messages || [];
      const payload = {
        prefix,
        projectId: id,
        playground: { ...getPlaygroundChatData, messages: [message, ...messages] },
      };

      dispatch(setPlayground(payload));
    }
  }, [getPlaygroundChatData]);

  useLoaderWhile(isLoadingGetPlaygroundChat);

  useLoaderWhile(isLoadingCreatePlaygroundChat);

  useEffect(() => {
    if (!isLastMessageLoading) {
      textareaRef?.current?.focus();
    }
  }, [isLastMessageLoading]);

  // Shown in place of the chat when it couldn't be created.
  let errorMessage = '';

  if (createPlaygroundChatError) {
    errorMessage =
      getErrorMessage(createPlaygroundChatError) === ERROR_MESSAGE_PLAYGROUND.BACKEND_ACTIVE_PLAN
        ? ERROR_MESSAGE_PLAYGROUND.FRIENDLY_ACTIVE_PLAN
        : ERROR_MESSAGE_PLAYGROUND.FRIENDLY_ERROR_MESSAGE;
  }

  const createMessage = (
    text: string,
    owner: 'user' | 'ai',
    chatData: Pick<PlaygroundData, '_id' | 'messages'>,
    loading = false,
  ): PlaygroundMessage => {
    const isInitialMessage = text === INITIAL_MESSAGE;
    const date = isInitialMessage ? chatData.messages?.[0]?.created_at || undefined : new Date();

    return {
      id: uuid(),
      project_id: id,
      body: text,
      created_at: moment.utc(date).format(ISO_DATE_FORMAT),
      loading,
      owner,
      chat_id: chatData._id,
    };
  };

  const onKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter' && event.shiftKey) {
      return;
    }

    if (event.key === 'Enter') {
      event.preventDefault();
      Boolean(inputValue) && applyMessages();
    }
  };

  const onButtonClick = () => {
    Boolean(inputValue) && applyMessages();
  };

  const applyMessages = async () => {
    const message = {
      user: createMessage(inputValue, 'user', { _id: chat._id }),
      ai: createMessage('', 'ai', { _id: chat._id }, true),
    };

    setInputValue('');
    dispatch(
      appendPlaygroundMessages({ prefix, projectId: id, messages: [message.user, message.ai] }),
    );
    await sendMessage(inputValue);
  };

  const scrollToBottom = () => {
    const scrollHeight = chatContainerRef.current.scrollHeight;
    const height = chatContainerRef.current.clientHeight;
    const maxScrollTop = scrollHeight - height;
    chatContainerRef.current.scrollTop = Math.max(maxScrollTop, 0);
  };

  const onRefresh = () => {
    dispatch(clearPlayground({ prefix, projectId: id }));
    createPlaygroundChat({ projectId: id });
    notification.success(`Playground chat refreshed.`);
  };

  const isReplyLocked =
    !projectData?.is_active ||
    isLastMessageLoading ||
    Boolean(escalatedAt) ||
    Boolean(resolvedAt) ||
    Boolean(errorMessage) ||
    disabled;

  return (
    <div
      className={clsx(
        'flex flex-auto flex-col items-center w-full overflow-hidden gap-2.5',
        className,
      )}
    >
      <div className="flex w-full justify-between card-header !min-h-fit">
        <div className={clsx('title cursor-default', titleClassName)}>
          {isTitle && <span className="!no-underline max-md:hidden">Playground</span>}
          {settings && !versionHidden && <PlaygroundSettingsVersion settings={settings} />}
        </div>
        <div className="flex items-center gap-2.5">
          <RefreshPlaygroundButton
            onClick={onRefresh}
            className={'self-end'}
            disabled={!projectData?.is_active || disabled}
          />
          {isFullScreenBtn && <FullScreenButton onClick={fullScreenBtnClick} />}
        </div>
      </div>
      <div
        ref={chatContainerRef}
        className={clsx(
          disabled && 'opacity-50',
          'flex flex-auto w-full  overflow-auto justify-center relative scroll-smooth max-w-[600px] h-1',
          chatContainerClassName,
        )}
      >
        {errorMessage ? (
          <div
            className={
              'flex text-center self-center text-sm font-medium text-ebony_opacity_30 shadow justify-self-center bg-white border border-light rounded-sm-5 p-5 whitespace-pre-line'
            }
          >
            {errorMessage}
          </div>
        ) : (
          <ul className="flex flex-col gap-[5px] p-1 w-full flex-1 lg:w-2/3">
            {chat?.messages?.map((message, index) => {
              if (isDateDivider(message)) {
                return (
                  <PlaygroundDateMessage date={message.value} key={`${message.value}-${index}`} />
                );
              }

              return message.owner === 'user' ? (
                <PlaygroundUserMessage
                  key={`${message.created_at}-${index}`}
                  message={message}
                  className={userMessageClassName}
                />
              ) : (
                <PlaygroundAIMessage
                  key={`${message.created_at}-${index}`}
                  message={message}
                  className={aIMessageClassName}
                />
              );
            })}
            {Boolean(escalatedAt) && <PlaygroundEscalatedMessage date={escalatedAt} />}
            {Boolean(resolvedAt) && <PlaygroundResolvedMessage date={resolvedAt} />}
          </ul>
        )}
      </div>
      <div
        className={clsx(
          (!projectData?.is_active || disabled) && 'opacity-50',
          'flex-initial w-full relative max-w-[600px]',
          replyContainerClassName,
        )}
      >
        <textarea
          ref={textareaRef}
          placeholder="Write a reply..."
          disabled={isReplyLocked}
          rows={3}
          value={inputValue}
          onChange={(e) => setInputValue(e.currentTarget.value)}
          className={
            'w-full border border-[#E3E6E8] !outline-none outline-ebony_opacity_10 resize-none rounded-sm-5 py-2.5 pl-5 pr-11 placeholder:text-[#E3E6E8] text-sm'
          }
          onKeyDown={onKeyDown}
        />
        <ReplyPlaygroundButton onClick={onButtonClick} disabled={isReplyLocked || !inputValue} />
      </div>
    </div>
  );
};
